import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiAlertTriangle, FiEdit2, FiPhone, FiTrash2 } from "react-icons/fi";
import fetchData from "../custom_functions/fetchData";
import patchData from "../custom_functions/patchData";
import deleteData from "../custom_functions/deleteData";
import { url } from "../utils/config";
import { title } from "../utils/style";
import AddTeamMember from "../child_components/AddTeamMember";

const statusFilters = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
];

function formatRole(role) {
  if (!role) return "-";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function formatStatus(status) {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function Team() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isNextPage, setIsNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editValues, setEditValues] = useState({ role: "viewer", status: "active" });
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [memberPendingDelete, setMemberPendingDelete] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [activeFilter]);

  useEffect(() => {
    fetchTeamMembers(page, activeFilter);
  }, [page, activeFilter]);

  function fetchTeamMembers(pageNumber, status) {
    setIsLoading(true);
    const params = new URLSearchParams();
    params.append("page", pageNumber);
    if (status !== "all") {
      params.append("status", status);
    }

    fetchData(
      `${url}/team_members?${params.toString()}`,
      (error) => {
        setIsLoading(false);
        toast.error(error?.response?.data?.message || "Unable to load team members", { autoClose: 3000 });
      },
      (response) => {
        setTeamMembers(response?.data || []);
        setIsNextPage(Boolean(response?.isNextPage));
        setEditingMemberId(null);
        setEditValues({ role: "viewer", status: "active" });
        setIsLoading(false);
      },
      false
    );
  }

  function startEditing(member) {
    if (!member || !member.id) return;
    setEditingMemberId(member.id);
    // Normalize status to lowercase for consistent comparison with option values
    const normalizedStatus = String(member?.status || "active").toLowerCase();
    setEditValues({
      role: member?.role || "viewer",
      status: normalizedStatus === "inactive" ? "inactive" : "active",
    });
  }

  function cancelEditing() {
    setEditingMemberId(null);
    setEditValues({ role: "viewer", status: "active" });
  }

  function handleEditValueChange(event) {
    const { name, value } = event.target;
    setEditValues((prev) => ({ ...prev, [name]: value }));
  }

  function saveMemberUpdates(memberId) {
    if (!memberId) return;
    setUpdatingId(memberId);
    patchData(
      `${url}/team_members/${memberId}`,
      { role: editValues.role, status: editValues.status },
      (error) => {
        setUpdatingId(null);
        toast.error(error?.response?.data?.message || "Unable to update team member", { autoClose: 3000 });
      },
      () => {
        setUpdatingId(null);
        toast.success("Team member updated", { autoClose: 3000 });
        cancelEditing();
        fetchTeamMembers(page, activeFilter);
      },
      false
    );
  }

  function removeTeamMember(memberId) {
    if (!memberId) return;
    setDeletingId(memberId);
    deleteData(
      `${url}/team_members/${memberId}`,
      (error) => {
        setDeletingId(null);
        toast.error(error?.response?.data?.message || "Unable to delete team member", { autoClose: 3000 });
      },
      () => {
        setDeletingId(null);
        toast.success("Team member removed", { autoClose: 3000 });
        if (editingMemberId === memberId) {
          cancelEditing();
        }
        setMemberPendingDelete(null);
        fetchTeamMembers(page, activeFilter);
      },
      false
    );
  }

  const statusBadges = useMemo(
    () => ({
      active: "text-emerald-700 bg-emerald-50 border border-emerald-100",
      inactive: "text-amber-700 bg-amber-50 border border-amber-100",
    }),
    []
  );

  const hasMembers = teamMembers && teamMembers.length > 0;
  const canGoBack = page > 1;
  const pendingDeleteId = memberPendingDelete?.id;
  const isDeletingPendingMember = Boolean(pendingDeleteId) && deletingId === pendingDeleteId;

  function dismissDeletePrompt() {
    if (memberPendingDelete && deletingId === memberPendingDelete.id) return;
    setMemberPendingDelete(null);
  }

  return (
    <div className="p-4" style={{ backgroundColor: "#e6f9ec", borderRadius: "12px" }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className={title}>Teams</div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-white shadow-base transition-all hover:bg-primary-dark"
          >
            Add Team Member
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {statusFilters.map((filter) => {
          const isActive = activeFilter === filter.id;
          return (
            <button
              key={filter.id}
              type="button"
              onClick={() => setActiveFilter(filter.id)}
              className={`px-5 py-2 rounded-full border text-sm font-semibold transition-colors ${isActive
                  ? "border-emerald-500 bg-emerald-600 text-white shadow-[0_10px_25px_rgba(16,185,129,0.25)]"
                  : "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                }`}
            >
              {filter.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-4">
        {isLoading && (
          <div className="rounded-2xl border border-emerald-100 bg-white px-6 py-8 text-center text-emerald-600 shadow-[0_18px_40px_rgba(22,101,52,0.12)]">
            Loading team members...
          </div>
        )}

        {!isLoading && !hasMembers && (
          <div className="rounded-2xl border border-dashed border-emerald-200 bg-white px-6 py-16 text-center text-emerald-500 shadow-[0_12px_30px_rgba(22,101,52,0.12)]">
            No team members found.
          </div>
        )}

        {!isLoading && hasMembers &&
          teamMembers.map((member) => {
            const memberId = member?.id || `${member?.email}-${member?.name}`;
            const activeId = member?.id;
            const isEditing = editingMemberId === activeId;
            const isSaving = updatingId === activeId;
            const isDeleting = deletingId === activeId;
            const canMutate = Boolean(activeId);

            return (
              <div
                key={memberId}
                className="w-full rounded-2xl border border-emerald-100 bg-white px-5 py-5 shadow-[0_18px_40px_rgba(22,101,52,0.12)]"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-lg font-bold text-emerald-700">
                      {(member?.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-emerald-900">{member?.name || "Unnamed"}</div>
                      <div className="text-sm text-emerald-500">{member?.email || "No email provided"}</div>
                      <div className="text-sm text-emerald-500 mt-1 flex items-center gap-2">
                        <FiPhone className="text-emerald-400" size={14} />
                        <span>{member?.phone || "No contact provided"}</span>
                      </div>
                      {isEditing ? (
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          <div>
                            <label className="block text-xs font-semibold text-emerald-700 mb-1 uppercase">Role</label>
                            <select
                              name="role"
                              value={editValues.role}
                              onChange={handleEditValueChange}
                              className="w-full rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            >
                              <option value="manager">Manager</option>
                              <option value="viewer">Viewer</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-emerald-700 mb-1 uppercase">Status</label>
                            <select
                              name="status"
                              value={editValues.status}
                              onChange={handleEditValueChange}
                              className="w-full rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 inline-flex items-center gap-2 text-xs text-emerald-600">
                          <span className="font-semibold uppercase tracking-wide">Role</span>
                          <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700 font-medium">
                            {formatRole(member?.role)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-3 self-start lg:self-center">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          isEditing ? cancelEditing() : startEditing(member)
                        }
                        className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${isEditing ? "bg-emerald-600 text-white" : "border border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                          }`}
                        aria-label="Edit team member"
                        disabled={isDeleting || !canMutate}
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setMemberPendingDelete(member)}
                        disabled={isDeleting || !canMutate}
                        className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${isDeleting
                            ? "bg-rose-100 text-rose-300 cursor-not-allowed"
                            : "border border-rose-200 text-rose-500 hover:bg-rose-50"
                          }`}
                        aria-label="Delete team member"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                    {isEditing ? (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <button
                          type="button"
                          className={`px-4 py-2 rounded-full text-sm font-semibold text-white ${isSaving ? "bg-emerald-300" : "bg-emerald-600 hover:bg-emerald-700"
                            }`}
                          onClick={() => saveMemberUpdates(activeId)}
                          disabled={isSaving || !canMutate}
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          className="px-4 py-2 rounded-full border border-emerald-200 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                          onClick={cancelEditing}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-emerald-500">Status</span>
                        <span
                          className={`rounded-full px-3 py-1 text-sm font-semibold ${statusBadges[member?.status] || "text-slate-600 bg-slate-100 border border-slate-200"
                            }`}
                        >
                          {formatStatus(member?.status)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm font-medium text-emerald-600">Page {page}</div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => canGoBack && setPage((prev) => Math.max(prev - 1, 1))}
            disabled={!canGoBack || isLoading}
            className={`px-5 py-2 rounded-full border text-sm font-semibold transition-colors ${canGoBack && !isLoading
                ? "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
              }`}
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => isNextPage && setPage((prev) => prev + 1)}
            disabled={!isNextPage || isLoading}
            className={`px-5 py-2 rounded-full border text-sm font-semibold transition-colors ${isNextPage && !isLoading
                ? "border-emerald-500 bg-emerald-600 text-white shadow-[0_10px_25px_rgba(16,185,129,0.25)] hover:bg-emerald-700"
                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
              }`}
          >
            Next
          </button>
        </div>
      </div>

      {showAddModal && (
        <AddTeamMember
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            setPage(1);
            fetchTeamMembers(1, activeFilter);
          }}
          selectedFilter={activeFilter}
        />
      )}

      {memberPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-[0_25px_50px_rgba(22,101,52,0.18)]">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-500">
                <FiAlertTriangle size={22} />
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold text-emerald-900">Remove team member</div>
                <p className="mt-1 text-sm text-emerald-600">
                  Are you sure you want to remove {memberPendingDelete?.name || "this member"} from the team? This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                onClick={dismissDeletePrompt}
                disabled={isDeletingPendingMember}
                className="text-sm font-semibold text-emerald-400 transition-colors hover:text-emerald-600 disabled:text-emerald-200"
              >
                X
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={dismissDeletePrompt}
                disabled={isDeletingPendingMember}
                className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => pendingDeleteId && removeTeamMember(pendingDeleteId)}
                disabled={isDeletingPendingMember}
                className={`rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors ${isDeletingPendingMember ? "bg-rose-300" : "bg-rose-500 hover:bg-rose-600"
                  }`}
              >
                {isDeletingPendingMember ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Team;
